import React, { useEffect, useRef, useId } from "react";
import "./Backdrop.scss";

export interface BackdropProps {
  children: any;
  isShow: boolean;
  isCloseIcon: boolean;
  onClose?: () => void;
  width?: string;
  parentClassName?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useDialogA11y(
  isShow: boolean,
  onClose?: () => void
) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isShow) return;

    previouslyFocused.current = document.activeElement as HTMLElement;

    const dialog = dialogRef.current;
    if (dialog) {
      const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      if (first) {
        first.focus();
      } else {
        dialog.setAttribute("tabindex", "-1");
        dialog.focus();
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

      // If nothing is focusable (e.g. dialog not actually visible), do not
      // swallow Tab — otherwise page keyboard navigation breaks.
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !dialog.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !dialog.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused.current && typeof previouslyFocused.current.focus === "function") {
        previouslyFocused.current.focus();
      }
    };
  }, [isShow, onClose]);

  return dialogRef;
}

const Backdrop: React.FC<BackdropProps> = (props) => {
  const dialogRef = useDialogA11y(props.isShow, props.onClose);
  const titleId = useId();

  return (
    <React.Fragment>
      {props.isShow && (
        <div className="backdrop">
          <div
            ref={dialogRef}
            className={`bdContent ${props?.parentClassName ?? ""} ${props.width ? props.width : ""}`}
            role="dialog"
            aria-modal="true"
           aria-labelledby={props.ariaLabelledBy || titleId}
           aria-label={props.ariaLabelledBy ? undefined : (props.ariaLabel || "Dialog")}
          >
            {props.isCloseIcon && (
              <button
                type="button"
                onClick={props.onClose}
                className="edel-icon-close closeIcon"
               aria-label="Close"
              />
            )}
            {props.children}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export const FullBackdrop: React.FC<BackdropProps> = (props) => {
  const dialogRef = useDialogA11y(props.isShow, props.onClose);
  const titleId = useId();

  return (
    <React.Fragment>
      {props.isShow && (
        <div className="fullBackdrop">
          <div
            ref={dialogRef}
            className="bdFullContent"
            role="dialog"
            aria-modal="true"
           aria-labelledby={props.ariaLabelledBy || titleId}
           aria-label={props.ariaLabelledBy ? undefined : (props.ariaLabel || "Dialog")}
          >
            {props.isCloseIcon && (
              <button
                type="button"
                onClick={props.onClose}
                className="edel-icon-close closeIcon"
               aria-label="Close"
              />
            )}
            {props.children}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
export default Backdrop;
